import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';

const inr = (v) => (v == null ? '-' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const limit = (v) => (v == null ? 'Unlimited' : Number(v).toLocaleString('en-IN'));

/** Plain text with "## " headings and "- " bullets, shown as headings and lists. Nothing is treated as HTML. */
export function Terms({ text }) {
  const blocks = [];
  let list = null;
  (text || '').split('\n').forEach((raw, i) => {
    const line = raw.trim();
    if (line.startsWith('- ')) {
      if (!list) { list = []; blocks.push({ type: 'list', items: list, key: `l${i}` }); }
      list.push(line.slice(2));
    } else {
      list = null;
      if (line.startsWith('## ')) blocks.push({ type: 'h', text: line.slice(3), key: `h${i}` });
      else if (line) blocks.push({ type: 'p', text: line, key: `p${i}` });
    }
  });
  return (
    <div>
      {blocks.map((b) => {
        if (b.type === 'h') return <h6 key={b.key} className="mt-4 mb-2">{b.text}</h6>;
        if (b.type === 'p') return <p key={b.key} className="mb-2">{b.text}</p>;
        return <ul key={b.key} className="mb-2">{b.items.map((t, i) => <li key={i}>{t}</li>)}</ul>;
      })}
    </div>
  );
}

function capacityLines(p) {
  const lines = [];
  lines.push(`${limit(p.maxWarehouses)} warehouse${p.maxWarehouses === 1 ? '' : 's'}, ${limit(p.maxRetailStores)} retail store${p.maxRetailStores === 1 ? '' : 's'}`);
  if (p.maxProductionUnits) lines.push(`${limit(p.maxProductionUnits)} production units`);
  lines.push(`${p.maxDocumentsPerMonth == null ? 'Unlimited' : Number(p.maxDocumentsPerMonth).toLocaleString('en-IN')} documents / month`);
  lines.push(`${limit(p.maxUsers)} users`);
  return lines;
}

/**
 * Account Management > Plans & Pricing: the plans an account manager can sell, built from the live plan catalog, the
 * reseller terms, and a customer quote (no margins) they can print.
 */
function AccountManagerPlans() {
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState('');
  const [quotePlanId, setQuotePlanId] = useState(null);

  useEffect(() => {
    axios.get('/api/account-manager/plans')
      .then((r) => setData(r.data))
      .catch((e) => setProblem(typeof e.response?.data === 'string' ? e.response.data : 'Could not load the plans'));
  }, []);

  if (problem) return <Alert variant="warning" className="mt-3">{problem}</Alert>;
  if (!data) return <div className="d-flex justify-content-center mt-4"><Spinner animation="border" role="status" /></div>;

  const plans = data.plans || [];
  const catalog = data.featureCatalog || [];
  const quotePlan = plans.find((p) => p.id === quotePlanId);

  return (
    <div className="mt-3">
      <p className="text-muted">
        The plans you can sell. Prices are per organization, exclusive of GST. This page always shows the plans as they are
        set up in ibigdo today.
      </p>

      {plans.length === 0 ? (
        <Alert variant="info">No plans are available to sell yet.</Alert>
      ) : (
        <Row xs={1} md={2} xl={plans.length > 3 ? 4 : plans.length} className="g-3">
          {plans.map((p) => (
            <Col key={p.id}>
              <Card className="h-100">
                <Card.Body className="d-flex flex-column gap-2">
                  <div>
                    <h5 className="mb-0">{p.name}</h5>
                    {p.audience && <div className="text-muted small">{p.audience}</div>}
                  </div>
                  <div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 600 }}>{inr(p.priceYearly)}<span className="text-muted fs-6"> / year</span></div>
                    <div className="text-muted small">{p.yearlyOnly ? 'Yearly only' : `or ${inr(p.priceMonthly)} / month`}</div>
                  </div>
                  <ul className="small mb-0 ps-3">
                    {capacityLines(p).map((t) => <li key={t}>{t}</li>)}
                    <li>{(p.features || []).length} of {catalog.length} optional features</li>
                    {p.accountManagerResponseDays != null && <li>Account manager first response within {p.accountManagerResponseDays} business day{p.accountManagerResponseDays === 1 ? '' : 's'}</li>}
                    {p.supportResponseDays != null && <li>ibigdo backs that up within {p.supportResponseDays} business day{p.supportResponseDays === 1 ? '' : 's'}</li>}
                  </ul>
                  <div className="mt-auto small">
                    {p.onboardingFee != null
                      ? <>Onboarding {inr(p.onboardingFee)} one-time <Badge bg={p.onboardingRequired ? 'warning' : 'secondary'} text={p.onboardingRequired ? 'dark' : undefined}>{p.onboardingRequired ? 'Required' : 'Optional'}</Badge></>
                      : 'No onboarding fee'}
                  </div>
                  <Button size="sm" variant="outline-primary" onClick={() => setQuotePlanId(p.id)}>Customer quote</Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {plans.length > 0 && (
        <Card className="mt-4">
          <Card.Header><h6 className="mb-0">What each plan includes</h6></Card.Header>
          <Card.Body>
            <p className="text-muted small">
              Every plan includes purchasing, stock, sales, GST invoicing with credit and debit notes, vendor invoices with input tax
              credit, accounts, receivables, GSTR-1 and GSTR-3B workings, Tally export and import, and the standard roles. These
              features differ:
            </p>
            <Table size="sm" bordered responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Feature</th>
                  {plans.map((p) => <th key={p.id} className="text-center">{p.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {catalog.map((f) => (
                  <tr key={f.key}>
                    <td>{f.label}</td>
                    {plans.map((p) => (
                      <td key={p.id} className="text-center">
                        {(p.features || []).includes(f.key) ? <span className="text-success fw-bold">&#10003;</span> : <span className="text-muted">&ndash;</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {data.terms && (
        <Card className="mt-4">
          <Card.Header><h6 className="mb-0">Add-ons, discounts and reseller terms</h6></Card.Header>
          <Card.Body>
            <Terms text={data.terms} />
            {data.termsUpdatedAt && <div className="text-muted small mt-3">Updated {formatDate(data.termsUpdatedAt)}</div>}
          </Card.Body>
        </Card>
      )}

      <Modal show={Boolean(quotePlan)} onHide={() => setQuotePlanId(null)} size="lg" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>Customer quote</Modal.Title>
          <div className="print-actions">
            <Button variant="primary" className="me-2" onClick={() => window.print()}>Print</Button>
            <Button variant="secondary" onClick={() => setQuotePlanId(null)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          {quotePlan && <Quote plan={quotePlan} catalog={catalog} />}
        </Modal.Body>
      </Modal>
    </div>
  );
}

/** What a customer sees: the plan, what it includes and what it costs. No margins or reseller terms. */
function Quote({ plan, catalog }) {
  const included = catalog.filter((f) => (plan.features || []).includes(f.key));
  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div className="print-content">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h3 className="mb-0">ibigdo</h3>
          <div className="text-muted">Subscription quote</div>
        </div>
        <div className="text-muted small text-end">Date: {today}</div>
      </div>
      <h4>{plan.name} plan</h4>
      {plan.audience && <p className="text-muted">{plan.audience}</p>}
      <Table bordered size="sm">
        <tbody>
          <tr><td>Annual subscription</td><td className="text-end fw-bold">{inr(plan.priceYearly)}</td></tr>
          {!plan.yearlyOnly && <tr><td>Or monthly</td><td className="text-end">{inr(plan.priceMonthly)} / month</td></tr>}
          {plan.onboardingFee != null && (
            <tr><td>Onboarding (one-time, {plan.onboardingRequired ? 'required' : 'optional'})</td><td className="text-end">{inr(plan.onboardingFee)}</td></tr>
          )}
        </tbody>
      </Table>
      <p className="small text-muted">Prices are exclusive of GST.</p>
      <h6 className="mt-4">Capacity</h6>
      <ul>{capacityLines(plan).map((t) => <li key={t}>{t}</li>)}</ul>
      <h6 className="mt-3">Included</h6>
      <p className="mb-1">
        Purchasing, stock, sales, GST invoicing with credit and debit notes, vendor invoices with input tax credit, accounts,
        receivables, GSTR-1 and GSTR-3B workings, Tally export and import, and the standard roles.
      </p>
      {included.length > 0 && (
        <>
          <p className="mb-1">Also on this plan:</p>
          <ul>{included.map((f) => <li key={f.key}>{f.label}</li>)}</ul>
        </>
      )}
      {plan.supportResponseDays != null && (
        <p className="small">Every customer has a dedicated account manager{plan.accountManagerResponseDays != null && <>, who gives a first response within {plan.accountManagerResponseDays} business day{plan.accountManagerResponseDays === 1 ? '' : 's'}</>}. ibigdo replies to any ticket within {plan.supportResponseDays} business day{plan.supportResponseDays === 1 ? '' : 's'} if the account manager has not.</p>
      )}
    </div>
  );
}

export default AccountManagerPlans;
